import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Check, ChevronsUpDown, X } from 'lucide-react';
import { z } from 'zod';

import { PolicyReviewSingleCreateRequest } from '@/types/service/policyUserReview';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { SimpleDatePicker } from '@/components/shared/SimpleDatePicker';

import { useGetPoliciesQuery } from '@/store/api/policyApi';
import { useGetUserListQuery } from '@/store/api/userManagementApi';

import { zodResolver } from '@hookform/resolvers/zod';

const singleReviewSchema = z.object({
  policy_id: z.number({ required_error: 'Policy is required' }).min(1, 'Policy is required'),
  user_id: z.number({ required_error: 'User is required' }).min(1, 'User is required'),
  review_period_start: z.date({ required_error: 'Start date is required' }),
  review_period_end: z.date({ required_error: 'End date is required' }),
});

type SingleReviewFormData = z.infer<typeof singleReviewSchema>;

interface CreateSingleReviewFormProps {
  onSubmit: (data: PolicyReviewSingleCreateRequest) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CreateSingleReviewForm = ({
  onSubmit,
  onCancel,
  isSubmitting,
}: CreateSingleReviewFormProps) => {
  const { data: policiesData, isLoading: policiesLoading } = useGetPoliciesQuery();
  const { data: usersData, isLoading: usersLoading } = useGetUserListQuery();

  const [policyOpen, setPolicyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const {
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SingleReviewFormData>({
    resolver: zodResolver(singleReviewSchema),
    defaultValues: {
      policy_id: 0,
      user_id: 0,
      review_period_start: new Date(),
      review_period_end: (() => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date;
      })(),
    },
  });

  const policyId = watch('policy_id');
  const userId = watch('user_id');
  const reviewPeriodStart = watch('review_period_start');
  const reviewPeriodEnd = watch('review_period_end');

  const handleFormSubmit = async (data: SingleReviewFormData) => {
    try {
      // Convert dates to ISO date strings for API
      const payload: PolicyReviewSingleCreateRequest = {
        policy_id: data.policy_id,
        user_id: data.user_id,
        review_period_start: data.review_period_start.toISOString().split('T')[0],
        review_period_end: data.review_period_end.toISOString().split('T')[0],
      };
      await onSubmit(payload);
      reset();
    } catch (error) {
      // Error handled by parent
    }
  };

  const policies = policiesData?.policies || [];
  const users = usersData?.users || [];

  const selectedPolicy = policies.find((p) => p.id === policyId);
  const selectedUser = users.find((u) => u.id === userId);

  return (
    <div className='border rounded-lg p-6 bg-green-50 space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-lg font-semibold'>Create Single Review</h3>
          <p className='text-sm text-muted-foreground'>
            Create an ad-hoc policy review for a specific user
          </p>
        </div>
        <Button variant='ghost' size='icon' onClick={onCancel} disabled={isSubmitting}>
          <X className='h-5 w-5' />
        </Button>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Policy Selection */}
          <div className='space-y-2'>
            <Label>
              Policy <span className='text-destructive'>*</span>
            </Label>
            <Popover open={policyOpen} onOpenChange={setPolicyOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  role='combobox'
                  className={cn('w-full justify-between', !policyId && 'text-muted-foreground')}
                  disabled={policiesLoading || isSubmitting}
                >
                  {selectedPolicy ? selectedPolicy.name : 'Select policy...'}
                  <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[400px] p-0'>
                <Command>
                  <CommandInput placeholder='Search policies...' />
                  <CommandList>
                    <CommandEmpty>No policy found.</CommandEmpty>
                    <CommandGroup>
                      {policies.map((policy) => (
                        <CommandItem
                          key={policy.id}
                          value={policy.name}
                          onSelect={() => {
                            setValue('policy_id', policy.id);
                            setPolicyOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              policy.id === policyId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {policy.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.policy_id && (
              <p className='text-sm text-destructive'>{errors.policy_id.message}</p>
            )}
          </div>

          {/* User Selection */}
          <div className='space-y-2'>
            <Label>
              User <span className='text-destructive'>*</span>
            </Label>
            <Popover open={userOpen} onOpenChange={setUserOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  role='combobox'
                  className={cn('w-full justify-between', !userId && 'text-muted-foreground')}
                  disabled={usersLoading || isSubmitting}
                >
                  {selectedUser
                    ? `${selectedUser.first_name} ${selectedUser.last_name}`
                    : 'Select user...'}
                  <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[400px] p-0'>
                <Command>
                  <CommandInput placeholder='Search users...' />
                  <CommandList>
                    <CommandEmpty>No user found.</CommandEmpty>
                    <CommandGroup>
                      {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={`${user.first_name} ${user.last_name} ${user.email}`}
                          onSelect={() => {
                            setValue('user_id', user.id);
                            setUserOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              user.id === userId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <div>
                            <div>
                              {user.first_name} {user.last_name}
                            </div>
                            <div className='text-xs text-muted-foreground'>{user.email}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {errors.user_id && <p className='text-sm text-destructive'>{errors.user_id.message}</p>}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Review Period Start */}
          <div>
            <SimpleDatePicker
              label='Review Period Start'
              value={reviewPeriodStart}
              onChange={(date) => date && setValue('review_period_start', date)}
              id='review_period_start'
              disabled={isSubmitting}
              required
            />
            {errors.review_period_start && (
              <p className='text-sm text-destructive mt-1'>{errors.review_period_start.message}</p>
            )}
          </div>

          {/* Review Period End */}
          <div>
            <SimpleDatePicker
              label='Review Period End'
              value={reviewPeriodEnd}
              onChange={(date) => date && setValue('review_period_end', date)}
              id='review_period_end'
              disabled={isSubmitting}
              required
            />
            {errors.review_period_end && (
              <p className='text-sm text-destructive mt-1'>{errors.review_period_end.message}</p>
            )}
          </div>
        </div>

        <p className='text-sm text-muted-foreground'>
          This will create an ad-hoc policy review for the selected user only.
        </p>

        <div className='flex gap-2 justify-end'>
          <Button type='button' variant='outline' onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type='submit' disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Single Review'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateSingleReviewForm;
